import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BarChart3, Clock, Users, Download, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  super_admin: "Direction Système", founder: "Fondateur", director: "Directeur",
  admin: "Administration", finance: "Finance", scolarite: "Scolarité",
  teacher: "Enseignant(e)", nurse: "Infirmerie", canteen: "Cantine", staff: "Personnel",
};

const fmtH = (h) => {
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  return `${hours}h${String(mins).padStart(2, "0")}`;
};

// Récapitulatif mensuel des heures de présence du personnel (pointages badge + GPS)
export default function PointageStatsPage() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/erp/pointage/monthly?month=${m}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(month); }, [month, load]);

  const exportCsv = () => {
    if (!data?.employees?.length) return;
    const head = "Employé;Rôle;Jours présents;Sessions;Dont badge;Heures totales;Moyenne h/jour";
    const lines = data.employees.map(r =>
      [r.employee_name, ROLE_LABELS[r.role] || r.role, r.days_present, r.sessions,
       r.badge_sessions, r.total_hours, r.avg_hours_per_day].join(";"));
    const blob = new Blob(["\ufeff" + [head, ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `pointages_${data.month}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const totals = data?.totals;

  return (
    <div className="space-y-6" data-testid="pointage-stats-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><BarChart3 className="h-3 w-3" /><span>RESSOURCES HUMAINES</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Statistiques de pointage</h1>
          <p className="text-slate-500 mt-1">Heures de présence mensuelles du personnel, calculées depuis les pointages badge au portail et GPS.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="w-44" data-testid="stats-month-input" />
          <Button onClick={exportCsv} variant="outline" className="gap-1" disabled={!data?.employees?.length}
            data-testid="stats-export-btn">
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-3 gap-4">
          {[[Users, "Employés pointés", totals.employees, "text-[#1E3A8A]", "stats-total-employees"],
            [Clock, "Heures cumulées", fmtH(totals.hours), "text-green-700", "stats-total-hours"],
            [BadgeCheck, "Sessions de pointage", totals.sessions, "text-orange-600", "stats-total-sessions"]]
            .map(([Icon, label, val, cls, tid]) => (
              <div key={label} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={tid}>
                <div className="text-xs uppercase text-slate-500 mb-1 flex items-center gap-1"><Icon className="h-3 w-3" />{label}</div>
                <div className={`text-3xl font-extrabold tabular-nums ${cls}`}>{val}</div>
              </div>
            ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Employé</th>
              <th className="px-4 py-3 text-left">Rôle</th>
              <th className="px-4 py-3 text-right">Jours présents</th>
              <th className="px-4 py-3 text-right">Sessions</th>
              <th className="px-4 py-3 text-right">Dont badge</th>
              <th className="px-4 py-3 text-right">Heures totales</th>
              <th className="px-4 py-3 text-right">Moy. / jour</th>
            </tr>
          </thead>
          <tbody>
            {(data?.employees || []).map(r => (
              <tr key={r.employee_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`stats-row-${r.employee_id}`}>
                <td className="px-4 py-2.5 font-semibold text-slate-800">{r.employee_name}</td>
                <td className="px-4 py-2.5 text-slate-500">{ROLE_LABELS[r.role] || r.role}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{r.days_present}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {r.sessions}
                  {r.open_sessions > 0 && <span className="text-[10px] text-orange-600 ml-1" title="Session(s) en cours sans sortie">({r.open_sessions} en cours)</span>}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{r.badge_sessions}</td>
                <td className="px-4 py-2.5 text-right font-bold text-[#1E3A8A] tabular-nums">{fmtH(r.total_hours)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{fmtH(r.avg_hours_per_day)}</td>
              </tr>
            ))}
            {!loading && data && data.employees.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="stats-empty">
                Aucun pointage enregistré sur {data.month}.
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Chargement…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
